import PincodeField, { pincodeLocation, emptyPincodeLocation } from '@/components/PincodeField';
import React, { useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { CheckCircle } from '@phosphor-icons/react';
import { SECTORS } from '@/utils/sector_options';
import SignupOnboardingShell from '@/components/SignupOnboardingShell';

const VCSignup = ({ user }) => {
  const [step, setStep] = useState(1);
  const [locationReady,setLocationReady]=useState(false);
  const [loading, setLoading] = useState(false);
  
  const isIncubator = user?.entity_type === 'incubator';
  const entityNoun = isIncubator ? 'incubator' : 'fund';
  const entityNounCap = isIncubator ? 'Incubator' : 'Fund';
  const accentClass = isIncubator ? 'warning' : 'primary';

  const [step1Data, setStep1Data] = useState({
    pincode:'',hq_city:'',hq_state:'',hq_country:'',hq_location:'',
    fund_name: '',
    role_at_fund: '',
    fund_size: '',
    fund_stage: '',
    portfolio_count: '',
    referral_source: '',
    entity_type: isIncubator ? 'incubator' : 'vc'
  });

  const [step2Data, setStep2Data] = useState({
    preferred_sectors: [],
    preferred_stages: [],
    typical_cheque_size: '',
    lead_or_coinvest: '',
    preferred_biz_models: [],
    thesis_description: ''
  });

  const handleStep1Submit = async () => {
    if (!locationReady || !step1Data.pincode) { toast.error('Enter a valid headquarters pincode'); return; }
    setLoading(true);
    try {
      const primary_geography = [step1Data.hq_city];
      // Previously collected as free-text geography.
      await api.post('/vc/signup/step1', { ...step1Data, primary_geography, fund_name: step1Data.fund_name.split(/\s+/).filter(Boolean).join(' ') });
      setStep(2);
      toast.success('Step 1 completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async () => {
    setLoading(true);
    try {
      await api.post('/vc/signup/step2', step2Data);
      setStep(3);
      toast.success('Investment thesis saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const toggleArrayItem = (array, item, setData, dataKey, max) => {
    const newArray = array.includes(item)
      ? array.filter((i) => i !== item)
      : [...array, item];
    if (max && newArray.length > max) {
      toast.error(`Select up to ${max} preferred sectors`);
      return;
    }
    setData((prev) => ({ ...prev, [dataKey]: newArray }));
  };

  if (step === 3) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-3xl w-full text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={32} className="text-success" weight="bold" />
          </div>
          <h2 className="text-4xl font-heading font-bold text-text-primary mb-4">
            Welcome to FounderBoard, {user.full_name}.
          </h2>
          <p className="text-lg text-text-secondary mb-12">
            Your command centre is ready. Here's how to get started.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface border border-border rounded-[10px] p-6 text-left">
              <h3 className="font-heading font-bold text-xl text-text-primary mb-3">Start discovering</h3>
              <p className="text-text-secondary mb-4">
                Browse startups already on the platform that match your thesis.
              </p>
              <button
                onClick={() => {
                  const u = JSON.parse(localStorage.getItem('user') || '{}');
                  u.onboarding_complete = true;
                  localStorage.setItem('user', JSON.stringify(u));
                  sessionStorage.setItem('pre_dashboard_destination', '/vc/discover');
                  window.location.assign('/pre-dashboard');
                }}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                data-testid="start-discovering-button"
              >
                Explore Discover
              </button>
            </div>

            <div className="bg-surface border border-border rounded-[10px] p-6 text-left">
              <h3 className="font-heading font-bold text-xl text-text-primary mb-3">Ask the AI anything</h3>
              <p className="text-text-secondary mb-4">
                Your AI chief of staff is ready. Try asking about your portfolio.
              </p>
              <button
                onClick={() => {
                  const u = JSON.parse(localStorage.getItem('user') || '{}');
                  u.onboarding_complete = true;
                  localStorage.setItem('user', JSON.stringify(u));
                  sessionStorage.setItem('pre_dashboard_destination', '/vc/chat');
                  window.location.assign('/pre-dashboard');
                }}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                data-testid="open-chat-button"
              >
                Open chat
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SignupOnboardingShell role={isIncubator ? 'incubator' : 'vc'} step={step}>

        {step === 1 && (
          <div>
            <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">About your {entityNoun}</h2>
            <p className="text-text-secondary mb-8">
              This helps us personalise your experience{isIncubator ? ' and surface relevant founders for your programs.' : ' and benchmark your portfolio.'}
            </p>

            <div className="bg-surface border border-border rounded-[10px] p-6 space-y-6">
              <div>
                <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                  {entityNounCap} name *
                </label>
                <input
                  type="text"
                  value={step1Data.fund_name}
                  onChange={(e) => setStep1Data({ ...step1Data, fund_name: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  placeholder={isIncubator ? 'e.g. T-Hub, Atal Incubation Centre' : 'e.g. Sequoia Capital India, Blume Ventures'}
                  data-testid="fund-name-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                  Your role at the {entityNoun} *
                </label>
                <select
                  value={step1Data.role_at_fund}
                  onChange={(e) => setStep1Data({ ...step1Data, role_at_fund: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="role-at-fund-select"
                >
                  <option value="">Select your role</option>
                  {isIncubator ? (
                    <>
                      <option value="CEO / Director">CEO / Director</option>
                      <option value="Program Manager">Program Manager</option>
                      <option value="Head of Investments">Head of Investments</option>
                      <option value="Operations Lead">Operations Lead</option>
                      <option value="Other">Other</option>
                    </>
                  ) : (
                    <>
                      <option value="General Partner (GP)">General Partner (GP)</option>
                      <option value="Managing Partner">Managing Partner</option>
                      <option value="Partner">Partner</option>
                      <option value="Principal">Principal</option>
                      <option value="Associate">Associate</option>
                      <option value="Solo GP / Angel">Solo GP / Angel</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                  {isIncubator ? 'Annual cohort budget' : 'Fund size'} *
                </label>
                <select
                  value={step1Data.fund_size}
                  onChange={(e) => setStep1Data({ ...step1Data, fund_size: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="fund-size-select"
                >
                  <option value="">Select {isIncubator ? 'budget' : 'fund size'}</option>
                  <option value="Under ₹50 Crore">Under ₹50Cr</option>
                  <option value="₹50-200 Crore">₹50Cr - ₹200Cr</option>
                  <option value="₹200-500 Crore">₹200Cr - ₹500Cr</option>
                  <option value="₹500 Crore - ₹1,000 Crore">₹500Cr - ₹1,000Cr</option>
                  <option value="Above ₹1,000 Crore">Above ₹1,000Cr</option>
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                  {isIncubator ? 'Number of startups currently incubated' : 'Number of active portfolio companies'} *
                </label>
                <select
                  value={step1Data.portfolio_count}
                  onChange={(e) => setStep1Data({ ...step1Data, portfolio_count: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="portfolio-count-select"
                >
                  <option value="">Select count</option>
                  <option value="1-5">1-5</option>
                  <option value="6-15">6-15</option>
                  <option value="16-30">16-30</option>
                  <option value="31-50">31-50</option>
                  <option value="50+">50+</option>
                </select>
              </div>

              <div>
                <PincodeField label="Headquarters pincode" required value={step1Data.pincode} onChange={pin=>setStep1Data(current=>({...current,...emptyPincodeLocation(pin)}))} onResolve={location=>setStep1Data(current=>({...current,...pincodeLocation(location)}))} onStatusChange={setLocationReady} disabled={loading} testId="primary-geography-input"/>
              </div>

              <button
                onClick={handleStep1Submit}
                disabled={!step1Data.fund_name.trim() || !step1Data.role_at_fund || !step1Data.fund_size || !step1Data.portfolio_count || !locationReady || !step1Data.pincode || loading}
                className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="step1-continue-button"
              >
                {loading ? 'Saving...' : 'Continue →'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">{isIncubator ? 'Your program focus' : 'Your investment thesis'}</h2>
            <p className="text-text-secondary mb-8">
              We use this to personalise your Discover feed and {isIncubator ? 'match relevant founders to your programs.' : 'match you with the most relevant startups.'}
            </p>

            <div className="bg-surface border border-border rounded-[10px] p-6 space-y-6">
              <div>
                <label className="block text-xs uppercase tracking-wider text-text-secondary mb-3">
                  Preferred sectors * (select up to 6)
                  <span className="ml-2 normal-case text-text-muted">{step2Data.preferred_sectors.length}/6 selected</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {SECTORS.map((sector) => (
                    <button
                      key={sector}
                      type="button"
                      onClick={() => toggleArrayItem(step2Data.preferred_sectors, sector, setStep2Data, 'preferred_sectors', 6)}
                      className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                        step2Data.preferred_sectors.includes(sector)
                          ? 'bg-primary border-primary text-white'
                          : 'bg-background border-border text-text-secondary hover:border-primary/50'
                      }`}
                      data-testid={`sector-${sector.toLowerCase().replace(/ /g, '-')}-button`}
                    >
                      {sector}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-text-secondary mb-3">
                  Preferred investment stages *
                </label>
                <div className="flex flex-wrap gap-2">
                  {['Pre-seed', 'Seed', 'Pre-Series A', 'Series A', 'Series B', 'Growth'].map((stage) => (
                    <button
                      key={stage}
                      type="button"
                      onClick={() => toggleArrayItem(step2Data.preferred_stages, stage, setStep2Data, 'preferred_stages')}
                      className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                        step2Data.preferred_stages.includes(stage)
                          ? 'bg-primary border-primary text-white'
                          : 'bg-background border-border text-text-secondary hover:border-primary/50'
                      }`}
                      data-testid={`stage-${stage.toLowerCase().replace(/ /g, '-')}-button`}
                    >
                      {stage}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                  Typical cheque size *
                </label>
                <select
                  value={step2Data.typical_cheque_size}
                  onChange={(e) => setStep2Data({ ...step2Data, typical_cheque_size: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="cheque-size-select"
                >
                  <option value="">Select cheque size</option>
                  <option value="Under ₹50 Lakhs">Under ₹50L</option>
                  <option value="₹50L - ₹2 Crore">₹50L - ₹2Cr</option>
                  <option value="₹2 - ₹10 Crore">₹2Cr - ₹10Cr</option>
                  <option value="₹10 - ₹50 Crore">₹10Cr - ₹50Cr</option>
                  <option value="Above ₹50 Crore">Above ₹50Cr</option>
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                  Do you lead rounds or co-invest? *
                </label>
                <select
                  value={step2Data.lead_or_coinvest}
                  onChange={(e) => setStep2Data({ ...step2Data, lead_or_coinvest: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="lead-coinvest-select"
                >
                  <option value="">Select option</option>
                  <option value="I typically lead rounds">I typically lead rounds</option>
                  <option value="I typically co-invest">I typically co-invest</option>
                  <option value="Both">Both, depending on the deal</option>
                </select>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 bg-surface border border-border text-text-primary rounded-lg font-medium hover:border-primary/50 transition-colors"
                  data-testid="step2-back-button"
                >
                  ← Back
                </button>
                <button
                  onClick={handleStep2Submit}
                  disabled={step2Data.preferred_sectors.length === 0 || step2Data.preferred_stages.length === 0 || !step2Data.typical_cheque_size || !step2Data.lead_or_coinvest || loading}
                  className="flex-1 bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="step2-continue-button"
                >
                  {loading ? 'Saving...' : 'Complete setup'}
                </button>
              </div>
            </div>
          </div>
        )}
    </SignupOnboardingShell>
  );
};

export default VCSignup;
